import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { JourCours, PointageEnseignantConfig, PresenceAbsence } from '@app/models/pointage-enseignant';
import moment from 'moment';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { DATE_FORMAT } from './planning.service';
import { PointageRubrique } from '@app/models/pointage';
import { CritereNotation, DossierPedagogique, Eleve, Evaluation, EvaluationConfig, EvaluationInscription } from '@app/models/enseignant';
import { BaseConfigService } from './config/base.service';
import { UserService } from './user.service';

export const FAKE_JOURCOURS: JourCours[] = [{
  date: moment(new Date()).format(DATE_FORMAT),
  heureDebut: "19:45:00.000",
  heureFin: "22:15:00.000",
  idCours: 95,
  idJourCours: 0,
  libelle: "Cours test1"
},
{
  date: moment(new Date()).format(DATE_FORMAT),
  heureDebut: "19:45:00.000",
  heureFin: "22:15:00.000",
  idCours: 95,
  idJourCours: 0,
  libelle: "Cours test4"
},
{
  date: moment(new Date()).format(DATE_FORMAT),
  heureDebut: "19:45:00.000",
  heureFin: "22:15:00.000",
  idCours: 95,
  idJourCours: 0,
  libelle: "Cours test5"
},
{
  date: moment(new Date()).format(DATE_FORMAT),
  heureDebut: "19:45:00.000",
  heureFin: "22:15:00.000",
  idCours: 95,
  idJourCours: 0,
  libelle: "Cours test6"
},
{
  date: moment(new Date()).add(7, 'd').format(DATE_FORMAT),
  heureDebut: "17:45:00.000",
  heureFin: "18:15:00.000",
  idCours: 96,
  idJourCours: 0,
  libelle: "Cours test2"
},
{
  date: moment(new Date()).add(1, 'M').format(DATE_FORMAT),
  heureDebut: "11:45:00.000",
  heureFin: "12:15:00.000",
  idCours: 97,
  idJourCours: 0,
  libelle: "Cours test3"
}]

@Injectable({
  providedIn: 'root'
})
export class EnseignantService {
  urlPointage = 'pointage/enseignant';
  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  private evaluationsCache$: Observable<Evaluation[]>;
  private coursSessionListCache$: Observable<any>;
  private coursListCache$: Observable<any>;
  private evaluationConfigCache$: Observable<EvaluationConfig>;

  configService = inject(BaseConfigService);
  userService = inject(UserService);

  constructor(protected http: HttpClient,) { }

  /****************************************************************
     * Pointage
     ****************************************************************/

  getCoursList(idEnseignant): Observable<any> {
    if (!this.coursListCache$) {
      this.coursListCache$ = this.http.get(`${this.urlPointage}/listCours?idEnseignant=${idEnseignant}`, this.httpOptions)
        .pipe(shareReplay(1));
    }
    return this.coursListCache$
  }

  getCoursSessionList(idEnseignant: number) {
    let params = new HttpParams();
    params = params.append('idEnseignant', idEnseignant);

    if (!this.coursSessionListCache$) {
      this.coursSessionListCache$ = this.http.get(`${this.urlPointage}/listCoursSession`, { ...this.httpOptions, params })
        .pipe(shareReplay(1));
    }

    return this.coursSessionListCache$;
  }

  getJourCours(idJourCour: number, idEnseigant: number) {
    return this.http.get(`${this.urlPointage}/${idEnseigant}/jourCours/${idJourCour}`, this.httpOptions);
  }

  getListStudents(idCours: number, idJourCours: number, idEnseignant: number) {
    let params = new HttpParams();
    params = params.append("idCours", idCours);
    params = params.append("idJourCours", idJourCours);
    params = params.append("idEnseignant", idEnseignant)
    return this.http.get(`${this.urlPointage}/listStudents`, { ...this.httpOptions, params })
  }

  getPresence(idCours: number, date: string, idJourCour: number, idEnseignant: number) {
    let params = new HttpParams();
    params = params.append("idCours", idCours);
    params = params.append("date", date);
    params = params.append("idJourCours", idJourCour);
    params = params.append("idEnseignant", idEnseignant);

    return this.http.get(`${this.urlPointage}/presenceAbsence`, { ...this.httpOptions, params })
  }

  getRubriques() {
    return this.http.get<PointageRubrique[]>(`pointage/rubriquesLasido`);
  }

  savePresencesAbsences(presencesAbsences: PresenceAbsence[], idEnseignant: number) {
    return this.http.post(`pointage/enseignant/save`, { presencesAbsences, idEnseignant });
  }

  isEditableDate(date: string | Date, config: PointageEnseignantConfig): boolean {
    const nbDaysLimiteSaisie = config.nbJourLimiteSaisie;
    const firstEditableDate = moment().subtract(nbDaysLimiteSaisie, 'd').format(DATE_FORMAT);

    if (!config.calendar.editabledAfterCurrentDay) {
      return firstEditableDate <= date && !(date > moment().format(DATE_FORMAT));
    }

    return firstEditableDate <= date;
  }

  /****************************************************************
   * Evaluation
   ****************************************************************/

  getEvaluationConfig() {
    if (!this.evaluationConfigCache$) {
      this.evaluationConfigCache$ = this.configService.get<EvaluationConfig>('evaluation-enseignant').pipe(
        shareReplay(1)
      );
    }
    return this.evaluationConfigCache$;
  }

  getAllEvaluationByIdEnseignant(idEnseignant: number): Observable<Evaluation[]> {
    if (!this.evaluationsCache$) {
      this.evaluationsCache$ = this.http.get<Evaluation[]>(`enseignant/${idEnseignant}/evaluations`, this.httpOptions).pipe(
        shareReplay(1)
      );
    }
    return this.evaluationsCache$;
  }

  getEvaluation(idEvaluation: number, idEnseignant: number): Observable<Evaluation> {
    return this.http.get<Evaluation>(`enseignant/${idEnseignant}/evaluation/${idEvaluation}`, this.httpOptions);
  }

  getEvaluationsInscriptionsByIdInscription(idEnseignant: number, idInscription: number) {
    return this.http.get<EvaluationInscription[]>(`enseignant/${idEnseignant}/${idInscription}/evaluationsInscriptions`)
  }

  deleteEvaluation(idEvaluation: number, idEnseignant: number) {
    return this.http.delete(`enseignant/${idEnseignant}/evaluation/${idEvaluation}`, this.httpOptions);
  }

  saveEvaluation(evaluation: Evaluation, idEnseignant: number): Observable<Evaluation> {
    return this.http.post<Evaluation>(`enseignant/${idEnseignant}/evaluation/${evaluation.idEvaluation}`, { evaluation }, this.httpOptions);
  }

  parseValeurCritereNotation(val: any, type: string): any {
    switch (type) {
      case 'checkbox': return val === '1';
      case 'numerique': return val !== '' && !isNaN(+val) ? +val : null;
      case 'texte': return val || '';
      case 'liste': return null;
      default: return val;
    }
  }

  calculerNoteGlobale(inscriptions: EvaluationInscription | EvaluationInscription[], evaluation: Evaluation) {
    const criteresNum = this.getCriteresNumeriques(evaluation);
    if (!criteresNum) { return; }

    const sommeCoefficients = criteresNum.reduce(
      (val, critere) => val + (critere.coefficient <= 0 ? 1 : critere.coefficient), 0
    );

    const calcul = (inscr: EvaluationInscription) => {
      inscr.noteGlobale = 0;
      if (sommeCoefficients > 0) {
        let notesPonderees = 0;
        criteresNum.forEach(critere => {
          if (inscr.criteresData && inscr.criteresData[critere.id]) {
            notesPonderees += (+inscr.criteresData[critere.id] * (critere.coefficient <= 0 ? 1 : critere.coefficient));
          }
        });
        inscr.noteGlobale = notesPonderees / sommeCoefficients;
        if ((inscr.noteGlobale + "").includes(".")) {
          inscr.noteGlobale = parseFloat(inscr.noteGlobale.toFixed(1));
        }
      }
    };

    if (Array.isArray(inscriptions)) {
      inscriptions.forEach(calcul);
    } else {
      calcul(inscriptions);
    }
  }

  getCriteresNumeriques(evaluation: Evaluation) {
    return evaluation?.criteresNotation?.filter(c => c.type == "numerique");
  }

  compare(val1: any, operateur: string, val2: any): boolean {
    switch (operateur) {
      case '==': case '=': return val1 == val2;
      case '===': return val1 === val2;
      case '!=': return val1 != val2;
      case '!==': return val1 !== val2;
      case '>': return val1 > val2;
      case '>=': return val1 >= val2;
      case '<': return val1 < val2;
      case '<=': return val1 <= val2;
      default: return false;
    }
  }

  actualiserReussite(inscriptions: EvaluationInscription | EvaluationInscription[], evaluation: Evaluation) {
    const process = (inscr: EvaluationInscription) => {
      inscr.reussiteExamen = true;

      for (const critere of evaluation.criteresNotation) {
        if (inscr.criteresData) {
          const valeur = inscr.criteresData[critere.id];
          if (!this.validateCritere(critere, valeur)) {
            inscr.reussiteExamen = false;
            break;
          }
        }
      }
    };

    if (Array.isArray(inscriptions)) {
      inscriptions.forEach(process);
    } else {
      process(inscriptions);
    }
  }

  getValeurInitialePourCritere(critere: CritereNotation) {
    switch (critere.type) {
      case 'numerique': return 0;
      case 'checkbox': return false;
      case 'liste': return null;
      case 'texte': return '';
      default: return null;
    }
  }

  // validateCritere(critere: any, value: any): boolean {

  //   if (critere.type === 'liste') {
  //     const valeurCritere = critere.valeurs.find(v => v.id == value);
  //     return valeurCritere ? !!valeurCritere.reussite : false;
  //   }

  //   if (critere.valeurReussite !== undefined && critere.valeurReussite !== null && critere.operateurReussite) {
  //     return this.compare(value, critere.operateurReussite, critere.valeurReussite);
  //   }

  //   // Si pas de condition de réussite, on considère le critère comme validé
  //   return true;
  // }

  validateCritere(critere, value) {

    let valid = false;

    if (critere.type != 'liste') {
      if (critere.valeurReussite) {
        let operateur = critere.operateurReussite;

        if (operateur == '=') operateur = '==';
        if (eval(`${JSON.stringify(value)} ${operateur} ${JSON.stringify(critere.valeurReussite)}`)) valid = true;
      }
      else valid = true;
    }
    else {
      let valeurCritere = critere.valeurs.find(v => v.id == value);

      if (valeurCritere) valid = valeurCritere.reussite;
    }

    return valid;
  }

  getInscriptionsByIdEnseignant(idEnseignant: number) {
    return this.http.get<Eleve[]>(`enseignant/${idEnseignant}/inscriptions`)
  }

  getHistoriqueEleve(idEnfant: number, idAdulte: number) {
    const idEnseignant = this.userService.currentUser.idEnseignant;
    return this.http.get<DossierPedagogique>(`enseignant/${idEnseignant}/historique-eleve?idEnfant=${idEnfant}&idAdulte=${idAdulte}`)
  }
}


